import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { toast } from "@/components/ui/toast"
import {
  activityPointsKeys,
  adminMembersKeys,
  titleSuggestionKeys,
} from "@/lib/query-keys"
import {
  type ApplyTitleSuggestionsInput,
  TitleSuggestionsApiError,
  titleSuggestionsApi,
} from "@/lib/title-suggestions-api"

export const useTitleSuggestionsQuery = () =>
  useQuery({
    queryKey: titleSuggestionKeys.current(),
    queryFn: titleSuggestionsApi.get,
  })

export const useApplyTitleSuggestionsMutation = (
  authenticatedMemberId: string | null,
) => {
  const queryClient = useQueryClient()

  const invalidateMembers = (
    memberIds: readonly string[],
    includeRoster: boolean,
  ) => {
    const invalidations = memberIds.map((memberId) =>
      queryClient.invalidateQueries({
        queryKey: adminMembersKeys.detail(memberId),
        exact: true,
      }),
    )
    if (includeRoster) {
      invalidations.push(
        queryClient.invalidateQueries({ queryKey: activityPointsKeys.all }),
      )
    }
    if (authenticatedMemberId && memberIds.includes(authenticatedMemberId)) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: ["profile", authenticatedMemberId] as const,
        }),
      )
    }
    return invalidations
  }

  return useMutation({
    mutationFn: titleSuggestionsApi.apply,
    retry: false,
    onSuccess: (response) => {
      const appliedIds = response.results
        .filter((result) => result.status === "applied")
        .map((result) => result.memberId)
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: titleSuggestionKeys.current(),
        }),
        ...invalidateMembers(appliedIds, appliedIds.length > 0),
      ]
      const attentionCount = response.results.filter(
        (result) =>
          result.status === "failed" ||
          result.status === "rank-conflict" ||
          result.status === "not-found",
      ).length
      toast.success(
        attentionCount === 0
          ? `Reviewed ${response.results.length} rank title suggestion${response.results.length === 1 ? "" : "s"}.`
          : `Reviewed ${response.results.length} suggestions; ${attentionCount} need attention.`,
      )
      return Promise.all(invalidations)
    },
    onError: (error: Error, input: ApplyTitleSuggestionsInput) => {
      const stale =
        error instanceof TitleSuggestionsApiError &&
        error.status === 409 &&
        error.code === "STALE_TITLE_SUGGESTIONS"
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: titleSuggestionKeys.current(),
        }),
        ...(stale ? [] : invalidateMembers(input.memberIds, true)),
      ]
      if (stale) {
        toast.warning(
          "Suggestions changed with the evaluation date or month. Review them again.",
        )
      } else {
        toast.error(
          "The apply result could not be confirmed. Review refreshed suggestions before trying again.",
        )
      }
      return Promise.all(invalidations)
    },
  })
}
